import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Card, Badge, Button, DataTable, Select, Input, Modal, ErrorState } from '../components/ui';
import { useApiQuery } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { combustibleService } from '../services/combustibleService';
import { MaquinasService } from '../services';
import { useFuelControlEnabled } from '../hooks/useFuelControlEnabled';
import type { EstadoConciliacion, TipoFotoTurno } from '../types/combustible';

const badgeVariantPorEstado: Record<EstadoConciliacion, 'green' | 'yellow' | 'red' | 'gray'> = {
  OK: 'green',
  DISCREPANCIA_LITROS: 'yellow',
  FOTOS_PENDIENTES: 'red',
  SIN_CONSUMO_ESPECIFICO: 'gray',
  PENDIENTE: 'gray',
};

const ControlCombustibleView: React.FC = () => {
  const { t } = useTranslation();
  const { habilitado, cargando: cargandoConfig } = useFuelControlEnabled();
  const { usuario } = useAuth();
  const puedeRegularizar = usuario?.tipo_usuario === 'supervisor' || usuario?.tipo_usuario === 'administrador';
  const [page, setPage] = useState(1);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [maquinaId, setMaquinaId] = useState<string>('');
  const [detalleReporteId, setDetalleReporteId] = useState<number | null>(null);

  // Edición (regularización) del supervisor sobre el detalle del turno.
  const [modoEdicion, setModoEdicion] = useState(false);
  const [nivelInicio, setNivelInicio] = useState('');
  const [nivelFin, setNivelFin] = useState('');
  const [litrosEntregados, setLitrosEntregados] = useState('');
  const [guardando, setGuardando] = useState(false);

  const perPage = 20;

  const { data: maquinasData } = useApiQuery(
    ['maquinas-para-filtro-combustible'],
    () => MaquinasService.getMaquinas(1, 100),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data, isLoading } = useApiQuery(
    ['combustible-conciliacion', page, fechaInicio, fechaFin, maquinaId],
    () => combustibleService.getConciliacion({
      page,
      per_page: perPage,
      fecha_inicio: fechaInicio || undefined,
      fecha_fin: fechaFin || undefined,
      maquina_id: maquinaId ? parseInt(maquinaId, 10) : undefined,
    }),
    { staleTime: 0, enabled: habilitado }
  );

  const { data: detalle, refetch: refetchDetalle } = useApiQuery(
    ['combustible-conciliacion-detalle', detalleReporteId],
    () => detalleReporteId ? combustibleService.getConciliacionDetalle(detalleReporteId) : Promise.resolve(null),
    { enabled: !!detalleReporteId && habilitado }
  );

  // Al abrir/cerrar el detalle, salir del modo edición y precargar los valores
  // actuales (niveles desde las fotos por tipo, litros desde el abastecimiento).
  useEffect(() => {
    setModoEdicion(false);
    const fotoInicio = detalle?.fotos.find(f => f.tipo === 'INICIO_TURNO');
    const fotoFin = detalle?.fotos.find(f => f.tipo === 'FIN_TURNO');
    setNivelInicio(fotoInicio?.nivel_declarado != null ? String(fotoInicio.nivel_declarado) : '');
    setNivelFin(fotoFin?.nivel_declarado != null ? String(fotoFin.nivel_declarado) : '');
    setLitrosEntregados(detalle?.abastecimiento?.litros_entregados != null ? String(detalle.abastecimiento.litros_entregados) : '');
  }, [detalle, detalleReporteId]);

  const guardarRegularizacion = async () => {
    if (!detalleReporteId) return;
    setGuardando(true);
    try {
      const payload: { nivel_inicio?: number; nivel_fin?: number; litros_entregados?: number } = {};
      const ni = parseFloat(nivelInicio.replace(',', '.'));
      const nf = parseFloat(nivelFin.replace(',', '.'));
      const le = parseFloat(litrosEntregados.replace(',', '.'));
      if (!isNaN(ni)) payload.nivel_inicio = ni;
      if (!isNaN(nf)) payload.nivel_fin = nf;
      if (!isNaN(le)) payload.litros_entregados = le;
      await combustibleService.regularizarTurno(detalleReporteId, payload);
      toast.success(t('fuel_control.detail.regularize_success'));
      setModoEdicion(false);
      await refetchDetalle();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || t('errors.save_error'));
    } finally {
      setGuardando(false);
    }
  };

  const reemplazarFoto = async (fotoId: number, file: File | undefined) => {
    if (!file) return;
    setGuardando(true);
    try {
      await combustibleService.editarEvidencia(fotoId, { fotoEvidencia: file });
      toast.success(t('fuel_control.detail.photo_replaced'));
      await refetchDetalle();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || t('errors.save_error'));
    } finally {
      setGuardando(false);
    }
  };

  const agregarFoto = async (tipo: TipoFotoTurno, file: File | undefined) => {
    if (!file || !detalleReporteId) return;
    setGuardando(true);
    try {
      await combustibleService.agregarEvidencia(detalleReporteId, { tipo, fotoEvidencia: file });
      toast.success(t('fuel_control.detail.photo_added'));
      await refetchDetalle();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || t('errors.save_error'));
    } finally {
      setGuardando(false);
    }
  };

  const maquinaOptions = useMemo(() => [
    { value: '', label: t('fuel_control.all_machines') },
    ...(maquinasData?.items || []).map(m => ({ value: String(m.id), label: m.codigo })),
  ], [maquinasData, t]);

  const columns = useMemo(() => [
    { key: 'maquina_codigo', title: t('fuel_control.table.machine') },
    { key: 'reporte_numero', title: t('fuel_control.table.report') },
    { key: 'fecha', title: t('fuel_control.table.date') },
    {
      key: 'horas_trabajadas',
      title: t('fuel_control.table.hours'),
      render: (value: number) => value.toFixed(1),
    },
    {
      key: 'litros_calculados',
      title: t('fuel_control.table.liters_calculated'),
      render: (value: number) => value.toFixed(1),
    },
    {
      key: 'litros_entregados',
      title: t('fuel_control.table.liters_delivered'),
      render: (value: number | null) => value !== null ? value.toFixed(1) : '-',
    },
    {
      key: 'nivel_inicio',
      title: t('fuel_control.table.level_start'),
      render: (value: number | null) => value !== null ? `${value}%` : '-',
    },
    {
      key: 'nivel_fin',
      title: t('fuel_control.table.level_end'),
      render: (value: number | null) => value !== null ? `${value}%` : '-',
    },
    {
      key: 'fotos_completas',
      title: t('fuel_control.table.photos'),
      render: (value: boolean) => value ? '✅' : '⏳',
    },
    {
      key: 'estado_conciliacion',
      title: t('fuel_control.table.status'),
      render: (value: EstadoConciliacion) => (
        <Badge variant={badgeVariantPorEstado[value]}>
          {t(`fuel_control.status.${value}`)}
        </Badge>
      ),
    },
    {
      key: 'reporte_id',
      title: '',
      render: (value: number) => (
        <Button size="sm" variant="ghost" onClick={() => setDetalleReporteId(value)}>
          {t('fuel_control.table.view_detail')}
        </Button>
      ),
    },
  ], [t]);

  if (!cargandoConfig && !habilitado) {
    return <ErrorState message={t('fuel_control.module_disabled')} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('fuel_control.title')}</h1>
        <p className="text-gray-600 mt-1">{t('fuel_control.subtitle')}</p>
      </div>

      <Card>
        <Card.Content>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              type="date"
              label={t('fuel_control.filters.date_from')}
              value={fechaInicio}
              onChange={(e) => { setFechaInicio(e.target.value); setPage(1); }}
            />
            <Input
              type="date"
              label={t('fuel_control.filters.date_to')}
              value={fechaFin}
              onChange={(e) => { setFechaFin(e.target.value); setPage(1); }}
            />
            <Select
              label={t('fuel_control.filters.machine')}
              options={maquinaOptions}
              value={maquinaId}
              onChange={(e) => { setMaquinaId(e.target.value); setPage(1); }}
            />
          </div>
        </Card.Content>
      </Card>

      <Card padding="none">
        <DataTable
          columns={columns}
          data={data?.items || []}
          isLoading={isLoading}
          emptyMessage={t('fuel_control.no_data')}
          pagination={data ? {
            currentPage: data.page,
            totalPages: data.pages,
            totalItems: data.total,
            itemsPerPage: data.per_page,
            onPageChange: setPage,
          } : undefined}
        />
      </Card>

      <Modal
        isOpen={!!detalleReporteId}
        onClose={() => setDetalleReporteId(null)}
        title={t('fuel_control.detail.title')}
      >
        {detalle && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('fuel_control.table.liters_calculated')}:</span>{' '}
                <span className="font-medium">{detalle.abastecimiento?.litros_calculados?.toFixed(1) ?? '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('fuel_control.table.liters_delivered')}:</span>{' '}
                <span className="font-medium">{detalle.abastecimiento?.litros_entregados?.toFixed(1) ?? '-'}</span>
              </div>
            </div>

            {detalle.alerta && (
              <div className="p-3 rounded bg-yellow-50 text-yellow-800 text-sm">{detalle.alerta}</div>
            )}

            {puedeRegularizar && (
              <div className="border-t border-gray-200 pt-4">
                {!modoEdicion ? (
                  <Button size="sm" variant="secondary" onClick={() => setModoEdicion(true)}>
                    {t('fuel_control.detail.regularize')}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">{t('fuel_control.detail.regularize')}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input
                        type="text"
                        inputMode="decimal"
                        label={t('fuel_control.table.level_start')}
                        value={nivelInicio}
                        onChange={(e) => setNivelInicio(e.target.value)}
                        placeholder="0-100"
                      />
                      <Input
                        type="text"
                        inputMode="decimal"
                        label={t('fuel_control.table.level_end')}
                        value={nivelFin}
                        onChange={(e) => setNivelFin(e.target.value)}
                        placeholder="0-100"
                      />
                      <Input
                        type="text"
                        inputMode="decimal"
                        label={t('fuel_control.table.liters_delivered')}
                        value={litrosEntregados}
                        onChange={(e) => setLitrosEntregados(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setModoEdicion(false)} disabled={guardando}>
                        {t('common.cancel')}
                      </Button>
                      <Button size="sm" onClick={guardarRegularizacion} loading={guardando}>
                        {t('common.save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('fuel_control.detail.photos')}</h4>
              {detalle.fotos.length === 0 && !(puedeRegularizar && modoEdicion) ? (
                <p className="text-sm text-gray-500">{t('fuel_control.detail.no_photos')}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {detalle.fotos.map(foto => (
                    <div key={foto.id} className="text-center">
                      <img
                        src={`/mava/storage/fuel_photos/${foto.ruta_foto_evidencia}`}
                        alt={foto.tipo}
                        className="w-full h-20 object-cover rounded border border-gray-200"
                      />
                      <span className="text-xs text-gray-500">{t(`fuel_control.tipo.${foto.tipo}`)}</span>
                      {puedeRegularizar && modoEdicion && (
                        <input
                          type="file"
                          accept="image/*"
                          className="mt-1 block w-full text-xs"
                          disabled={guardando}
                          onChange={(e) => reemplazarFoto(foto.id, e.target.files?.[0])}
                        />
                      )}
                    </div>
                  ))}
                  {puedeRegularizar && modoEdicion &&
                    (['INICIO_TURNO', 'FIN_TURNO'] as TipoFotoTurno[])
                      .filter(tp => !detalle.fotos.some(f => f.tipo === tp))
                      .map(tp => (
                        <div
                          key={tp}
                          className="text-center border border-dashed border-gray-300 rounded p-2 flex flex-col justify-center min-h-[6rem]"
                        >
                          <span className="text-xs text-gray-500 mb-1">{t(`fuel_control.tipo.${tp}`)}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="block w-full text-xs"
                            disabled={guardando}
                            onChange={(e) => agregarFoto(tp, e.target.files?.[0])}
                          />
                          <span className="text-[10px] text-gray-400 mt-1">{t('fuel_control.detail.add_photo')}</span>
                        </div>
                      ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ControlCombustibleView;
